import { action, computed, observable, runInAction } from "mobx";

import { DateTime } from "@bps/date-time";
import { IContextualMenuItem } from "@bps/fluent-ui";
import { NotFoundError } from "@bps/http-client";
import { InvoiceItemDto } from "@libs/gateways/billing/BillingGateway.dtos.ts";
import {
  AppointmentStatusCode,
  AppointmentTypeCode,
  CalendarEventType
} from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import {
  FormTemplateDTO,
  FormTemplateTypeCode
} from "@libs/gateways/forms/FormsGateway.dtos.ts";
import { ContactType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { createNewClaimDto } from "@modules/acc/screens/claim/components/utils.ts";
import { PatientCardIds } from "@modules/practice/screens/shared-components/types/patient-card-ids.enum.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";

import { ContextMenuItemsEnum } from "./components/booking-calendar-event/contextual-menu/ContextMenuItemsEnum.ts";

export enum AppointmentNoticeType {
  comment = "comment",
  interpreter = "interpreter",
  noCharge = "noCharge",
  providerComment = "providerComment"
}

export interface AppointmentNotice {
  isNew?: boolean;
  label: string | undefined;
  type: AppointmentNoticeType;
  shortDescription?: string;
  description: string;
}

export enum NoticeLabels {
  account = "Account",
  cardExpired = "Card expired",
  comment = "Appointment comment",
  interpreter = "Interpreter",
  noCharge = "No charge",
  providerComment = "Provider comment",
  meetingComment = "Meeting comment",
  unavailableComment = "Unavailable comment"
}

export class BookingCalendarEventModel {
  constructor(
    private root: IRootStore,
    public calendarEvent: CalendarEvent
  ) {}

  @observable
  public invoiceItems: InvoiceItemDto[] | undefined = undefined;

  @observable
  public preventDismiss: boolean = false;

  @observable
  public isNoChargeDialogVisible: boolean = false;

  @observable
  public openedAttendeeId: string | undefined = undefined;

  private get acc() {
    return this.root.acc;
  }

  private get billing() {
    return this.root.billing;
  }

  private get core() {
    return this.root.core;
  }

  private get practice() {
    return this.root.practice;
  }

  private get booking() {
    return this.root.booking;
  }

  private get routing() {
    return this.root.routing;
  }

  private get clinical() {
    return this.root.clinical;
  }
  @action setNoChargeDialogVisibility = (visibility: boolean) => {
    this.isNoChargeDialogVisible = visibility;
  };

  @action
  public setOpenedAttendeeId = (id: string | undefined) => {
    this.openedAttendeeId = id;
  };

  @action
  public setPreventDismiss = (value: boolean) => {
    this.preventDismiss = value;
  };

  @action loadInvoiceItems = async () => {
    if (!this.core.hasPermissions(Permission.InvoiceView)) return;

    const invoiceItems = await this.billing
      .fetchInvoiceItems({
        calendarEventIds: [this.calendarEvent.id],
        take: 1000
      })
      .then(x => x.results);
    runInAction(() => {
      this.invoiceItems = [...invoiceItems];
    });
  };

  getIsMenuDisable = (contactId: string) => {
    const status = this.getAppointmentOrAttendeeStatus(contactId);

    return (
      (status === AppointmentStatusCode.Finalised ||
        status === AppointmentStatusCode.Completed ||
        status === AppointmentStatusCode.DidNotAttend) &&
      !this.calendarEvent.userHasOpenEncounterWithPatient(contactId)
    );
  };

  getAppointmentOrAttendeeStatus = (contactId: string) => {
    const calendarEventAttendee = this.calendarEvent.activeAttendees?.find(
      a => a.attendeeId === contactId
    );
    return (
      calendarEventAttendee?.attendeeStatus || this.currentAppointmentStatus
    );
  };

  @computed
  get isRecordUpdateDisable() {
    return (
      !this.core.hasPermissions(Permission.EncounterWrite) ||
      this.currentAppointmentStatus === AppointmentStatusCode.Finalised ||
      this.currentAppointmentStatus === AppointmentStatusCode.Completed
    );
  }

  get currentAppointmentStatus() {
    return this.calendarEvent.appointmentStatus ?? AppointmentStatusCode.Booked;
  }

  get isAppointment() {
    return this.calendarEvent.type === CalendarEventType.Appointment;
  }
  get isPatientCalendarEvent() {
    return this.calendarEvent.isPatientCalendarEvent;
  }

  get isUnavailableType() {
    return this.calendarEvent.type === CalendarEventType.Unavailable;
  }

  get isSeries() {
    return (
      (this.isUnavailableType ||
        this.calendarEvent.type === CalendarEventType.Appointment) &&
      !!this.calendarEvent.calendarEventRecurrenceId
    );
  }

  get eventType() {
    return this.calendarEvent?.typeRef?.text || "event";
  }

  get pastDayAppointment() {
    return this.calendarEvent.startDateTime < DateTime.now();
  }

  get isFutureDayAppointment() {
    return this.calendarEvent.startDateTime.startOf("day") > DateTime.now();
  }

  get canInvoice() {
    return (
      !this.isFutureDayAppointment &&
      this.core.hasPermissions(Permission.InvoiceCreate)
    );
  }

  @computed get appointmentNotices() {
    return this.adoptAppointmentNoticesDataToClient();
  }

  @observable
  isStatusUpdating = false;

  get canUpdateStatus() {
    const isAfterTomorrow =
      this.calendarEvent.startDateTime >
      DateTime.now().plus({ days: 1 }).endOf("day");
    return (
      !isAfterTomorrow &&
      !this.isStatusUpdating &&
      this.core.hasPermissions(Permission.CalendarEventWrite)
    );
  }

  isRecurrenceDeleted = observable.box<boolean>(false);

  @computed get claim() {
    return this.calendarEvent.claim;
  }

  private isRecurrencePartOfSeriesDeleted = async () => {
    let isDeletedEvent = false;
    if (this.calendarEvent.calendarEventRecurrenceId) {
      try {
        await this.booking.getRecurrence(
          this.calendarEvent.calendarEventRecurrenceId
        );
      } catch (error) {
        if (error instanceof NotFoundError) {
          isDeletedEvent = true;
        }
      }
    }
    return isDeletedEvent;
  };

  private adoptAppointmentNoticesDataToClient = (): AppointmentNotice[] => {
    const appointmentNotices: AppointmentNotice[] = [];

    // Notices need to be in the following order:
    // 1. No Charge comment
    // 2. Provider comment
    // 3+ Every other notice sorted alphabetically by label

    //No charge
    if (this.calendarEvent.noChargeComment) {
      appointmentNotices.push({
        label: NoticeLabels.noCharge,
        type: AppointmentNoticeType.noCharge,
        description: this.calendarEvent.noChargeComment,
        isNew: true
      });
    }

    // Provider comment
    if (this.calendarEvent.providerComment) {
      appointmentNotices.push({
        label: NoticeLabels.providerComment,
        type: AppointmentNoticeType.providerComment,
        description: this.calendarEvent.providerComment,
        isNew: true
      });
    }
    let label: NoticeLabels = NoticeLabels.comment;
    if (this.calendarEvent.type === CalendarEventType.Meeting) {
      label = NoticeLabels.meetingComment;
    } else if (this.calendarEvent.type === CalendarEventType.Unavailable) {
      label = NoticeLabels.unavailableComment;
    }

    //Comment
    if (this.calendarEvent.content) {
      appointmentNotices.push({
        label,
        type: AppointmentNoticeType.comment,
        description: this.calendarEvent.content
      });
    }

    // Interpreter
    if (this.calendarEvent.contact?.interpreterLanguageValue)
      appointmentNotices.push({
        label: NoticeLabels.interpreter,
        type: AppointmentNoticeType.interpreter,
        description: this.calendarEvent.contact?.interpreterLanguageValue
      });

    return appointmentNotices;
  };

  public getStates = async () => {
    const isRecurrenceDeleted = await this.isRecurrencePartOfSeriesDeleted();
    runInAction(() => {
      this.isRecurrenceDeleted.set(isRecurrenceDeleted);
    });
  };

  public changeAppointmentStatus = async (
    appointmentStatus: AppointmentStatusCode,
    currentAppointmentStatus?: AppointmentStatusCode
  ) => {
    if (appointmentStatus === currentAppointmentStatus) {
      return;
    }

    runInAction(() => {
      this.isStatusUpdating = true;
    });

    try {
      return await this.booking.updateCalendarEventAppointmentStatus(
        this.calendarEvent.id,
        {
          appointmentStatus,
          calendarEventId: this.calendarEvent.id
        }
      );
    } finally {
      runInAction(() => {
        this.isStatusUpdating = false;
      });
    }
  };

  public showCancelEvent = () => {
    this.booking.ui.setCancelCalendarEventId(this.calendarEvent.id);

    if (this.calendarEvent.calendarEventRecurrenceId !== null) {
      this.booking.ui.setCancelCalendarEventRecurrenceId(
        this.calendarEvent.calendarEventRecurrenceId
      );
    }
    this.booking.ui.setShowCancelCalendarEventDialog(
      true,
      this.calendarEvent.id
    );
  };

  public onShowEdit = (cardId: PatientCardIds) => () => {
    if (!this.calendarEvent.contact || !this.calendarEvent.contactId) return;

    const hasPatientWritePermission = this.core.hasPermissions([
      Permission.PatientWrite
    ]);

    const id = this.calendarEvent.contactId;

    if (hasPatientWritePermission) this.practice.ui.showEditContact(cardId, id);
    else this.routing.push(routes.contacts.contact.path({ id }));
  };

  addClaimBeforeFormDeploy = async (
    selectedTemplate: FormTemplateDTO,
    currentContext: Record<string, string>
  ) => {
    //If no claim is in the context and it is an Acc form, then add an Acc claim to be associated with the form context
    if (
      selectedTemplate.code ===
        (FormTemplateTypeCode.acc || FormTemplateTypeCode.acc45Demographic) &&
      !currentContext.ClaimId
    ) {
      try {
        const { user, contact } = this.calendarEvent;
        if (!user || !contact) {
          throw new Error("Unknown user or patient");
        }

        const claimDto = await createNewClaimDto({
          root: this.root,
          providerUser: user,
          patient: contact
        });

        const episodeOfCare = await this.root.clinical.addEpisodeOfCare({
          patientId: contact.id
        });

        const newClaim = await this.root.acc.addClaim(
          claimDto,
          episodeOfCare.id
        );

        this.root.notification.success("A new claim has been created");
        await this.booking.updateCalendarEvent({
          id: this.calendarEvent.id,
          episodeOfCareId: episodeOfCare.id
        });
        currentContext.ClaimId = newClaim.id;
      } catch (error) {
        this.root.notification.error("Failed creating claim");
        throw error;
      }
    }
    return currentContext;
  };

  @computed get enabled() {
    return (
      this.calendarEvent.type === CalendarEventType.Appointment &&
      this.calendarEvent.contact?.type === ContactType.Patient
    );
  }

  checkConditionsMatch = async (contactId: string): Promise<boolean> => {
    // returns false if the encounter has a claim, but the calendar event has no claim or a different claim

    const openEncounter =
      this.calendarEvent.calendarEventExtension?.openEncounters?.find(
        x => x.userId === this.core.userId && x.patientId === contactId
      );

    if (!openEncounter) return true;

    const encounter = await this.clinical.getEncounter(
      openEncounter.encounterId
    );

    const { episodeOfCareId } = encounter;

    if (
      !episodeOfCareId ||
      episodeOfCareId === this.calendarEvent.episodeOfCareId
    )
      return true;

    const claimEpisodesOfCare = await this.acc.getClaimEpisodesOfCare({
      episodesOfCare: [episodeOfCareId]
    });

    if (!claimEpisodesOfCare.length) return true;

    const [{ claimId }] = claimEpisodesOfCare;

    const [calendarEventEpisodeOfCare, episodeOfCare] = await Promise.all([
      this.calendarEvent?.loadEpisodeOfCare(),
      this.clinical.getEpisodeOfCare(episodeOfCareId),
      this.acc.getClaim(claimId)
    ]);

    runInAction(() => {
      this.root.booking.ui.reviewConditionOptions = {
        calendarEvent: this.calendarEvent,
        encounter,
        encounterEpisodeOfCare: episodeOfCare,
        calendarEventEpisodeOfCare,
        encounterClaimEpisodeOfCare: claimEpisodesOfCare[0]
      };
    });

    return false;
  };

  getClinicalMenuItems = (attendeeId: string) => {
    const clinicalOnClick = async (contactId: string) => {
      if (!this.enabled) return;

      const conditionsMatch = await this.checkConditionsMatch(contactId);

      if (!conditionsMatch) return; // modal opens

      if (!this.root.booking.ui.reviewConditionOptions) {
        this.routing.push(
          routes.records.appointment.path({
            id: contactId,
            calendarEventId: this.calendarEvent.id
          }),
          this.routing.getStateWithFromQuery()
        );
      }
    };

    const appointmentTypeCode = this.calendarEvent.appointmentType?.code;
    const commandBarItemsList: IContextualMenuItem[] = [
      {
        key: "start-visit",
        id: "booking-event-context-start-visit-btn",
        text: this.calendarEvent.userHasOpenEncounterWithPatient(attendeeId)
          ? ContextMenuItemsEnum.CompleteConsult
          : ContextMenuItemsEnum.StartConsult,
        onClick: ev => {
          ev?.preventDefault();
          ev?.stopPropagation();
          clinicalOnClick(attendeeId);
        },
        disabled:
          this.getIsMenuDisable(attendeeId) ||
          !this.core.hasPermissions(Permission.EncounterWrite) ||
          (this.core.hasPermissions(Permission.LicencingAllowed) &&
            !this.core.hasPermissions(Permission.CreateConsultAllowed))
      },
      {
        key: "view-record",
        id: "booking-event-context-view-record-btn",
        text: ContextMenuItemsEnum.ViewRecord,
        onClick: () =>
          this.routing.push(
            routes.records.recordView.path({
              id: attendeeId
            }),
            this.routing.getStateWithFromQuery()
          )
      }
    ];

    appointmentTypeCode === AppointmentTypeCode.RecordUpdate &&
      (commandBarItemsList[0] = {
        key: "recordUpdate",
        id: "record-update-clinical-menu-item",
        text: ContextMenuItemsEnum.RecordUpdate,
        onClick: () => {
          clinicalOnClick(attendeeId);
        },
        disabled:
          this.isRecordUpdateDisable ||
          !this.core.hasPermissions(Permission.EncounterWrite)
      });

    return commandBarItemsList;
  };

  loadPatientNotices = async () => {
    const patientIds = this.calendarEvent.activeAttendees.map(
      a => a.attendeeId
    );

    await Promise.all([
      this.practice.getPatientNoticesByArgs({
        patientIds
      }),
      this.practice.loadSystemNotices(patientIds)
    ]);
  };
  updateNoChargeComment = async (
    noChargeComment: string | undefined,
    patientId: string
  ) => {
    if (this.calendarEvent) {
      const attendees = this.calendarEvent.dto.attendees.map(attendee => {
        if (attendee.attendeeId === patientId) {
          return {
            ...attendee,
            noChargeComment
          };
        }

        return attendee;
      });

      await this.root.booking.updateCalendarEvent({
        id: this.calendarEvent.id,
        attendees
      });
    }
  };

  startPatientMatchWorkflow = () => {
    this.booking.ui.startPatientMatchWorkflow(this.calendarEvent.id);
  };
}
